import { observer } from 'mobx-react-lite'
import type { BookkeepingAddManualQuickBooksConnectionDialogViewModel } from './BookkeepingAddManualQuickBooksConnectionDialogViewModel'
import { useId, type ReactNode } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { FormContainer } from '@/components/FormContainer'
import {
  formatBookkeepingPlatform,
  getBookkeepingPlatformLoginUrl,
} from '../../platform-connections/bookkeeping-platform-connection-formatters'

interface BookkeepingAddManualQuickBooksConnectionDialogContainerProps {
  vm: BookkeepingAddManualQuickBooksConnectionDialogViewModel
}

/**
 * The BookkeepingAddManualQuickBooksConnectionDialogContainer
 */
export const BookkeepingAddManualQuickBooksConnectionDialogContainer = observer(
  ({ vm }: BookkeepingAddManualQuickBooksConnectionDialogContainerProps) => {
    return (
      <BookkeepingAddManualConnectionDialog
        isOpen={vm.modal.showing}
        onClose={vm.modal.close}
        title={formatBookkeepingPlatform('QuickBooks')}
        description={<QuickBooksDescription />}
        openPlatformLabel="Open QuickBooks Online"
        loginUrl={getBookkeepingPlatformLoginUrl('QuickBooks')}
        onSubmit={vm.submit}
        isPending={vm.submit.pending}
        testId="manual-connection-dialog"
        submitButtonTestId="save-connection-button"
      >
        <TextFieldContainer
          model={vm}
          error={vm.validation.getError('companyName')}
          disabled={vm.submit.pending}
          field="companyName"
          label="Company name"
          testId="company-name-input"
        />
        <TextFieldContainer
          model={vm}
          type="number"
          field="companyId"
          error={vm.validation.getError('companyId')}
          disabled={vm.submit.pending}
          label="Company ID"
          hint="This is the ID that will be used to identify your business."
          testId="company-id-input"
        />
      </BookkeepingAddManualConnectionDialog>
    )
  },
)

interface BookkeepingAddManualConnectionDialogProps {
  children?: ReactNode
  title: string
  openPlatformLabel: string
  description: ReactNode
  loginUrl: string | null
  isOpen: boolean
  isPending?: boolean
  isDisabled?: boolean
  onClose?: () => unknown
  onSubmit?: () => unknown
  testId?: string
  submitButtonTestId?: string
}

/**
 * The BookkeepingAddManualConnectionDialog
 */
export function BookkeepingAddManualConnectionDialog({
  children,
  title,
  description,
  isOpen,
  isPending,
  loginUrl,
  openPlatformLabel,
  onSubmit,
  onClose,
  testId,
  submitButtonTestId,
}: BookkeepingAddManualConnectionDialogProps) {
  const formId = useId()
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Enter your {title} details</ModalDialogTitle>
        <ModalDialogDescription>{description}</ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <FormContainer
          compact
          id={formId}
          onSubmit={onSubmit}
          disabled={isPending}
        >
          {children}
        </FormContainer>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Save & continue"
            isPending={isPending}
            form={formId}
            type="submit"
            testId={submitButtonTestId}
          />
          {loginUrl && (
            <Button
              label={openPlatformLabel}
              href={loginUrl}
              variant="outline"
              target="_blank"
              isPending={isPending}
            />
          )}
          <Button
            label="Cancel"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

/**
 * The QuickBooksDescription
 */
export function QuickBooksDescription() {
  return (
    <>
      To find your company ID in QuickBooks Online , press{' '}
      <span className="text-strong"> Ctrl + Alt + ?</span> on Windows, or
      <span className="text-strong">Control + Option + ?</span> on Mac.
    </>
  )
}
